/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, Optional, Inject } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { catchError, mapTo, take, tap } from 'rxjs/operators';
import { EMPTY, merge, Observable, throwError } from 'rxjs';
import { LoggingService, Logger } from 'crimson/lib/logging/logging.service';
import { isEventUserActivated } from 'utils/lib/web-events/synthetic-events';
import { NvHigConfig, NV_HIG_CONFIG } from '../../hig.config';
import { ApiPatchConfigs } from './user-activation-guard.config';
import {
    ApiPatchConfig,
    UserActivationGuardType,
    UserActivationMessageOption,
    UserActivationType
} from './user-activation-guard.interface';

@Injectable({
    providedIn: 'root'
})
export class UserActivationGuardService {
    private logger: Logger;

    // whether the service is enabled
    private _enabled = false;
    get enabled() {
        return this._enabled;
    }
    set enabled(enabled: boolean) {
        this._enabled = enabled;
        this.logger.info(`Set UserActivationGuardService enabled: ${enabled}`);
    }

    private defaultWarningMessage: string;
    private guardedTypes: Map<UserActivationGuardType, UserActivationType> = new Map();
    private patchedTypes: UserActivationGuardType[] = [];

    private snackBarOpened = false;

    private warningHostElement: HTMLElement = null;
    private warningEl: HTMLElement = null;
    private labelEl: HTMLElement = null;
    private warningDismissTimer: number = null;
    private warningActive = false;

    private static readonly WARNING_CLASS = 'nv-activation-warning';
    private static readonly VISIBLE_CLASS = 'nv-activation-warning--visible';
    private static readonly AUTO_DISMISS_MS = 5000;

    private storedOriginalImplementation = new Map<
        string,
        ApiPatchConfig & {
            original: (...args: any[]) => any;
        }
    >();

    constructor(
        loggingService: LoggingService,
        private snackbarService: MatSnackBar,
        private translateService: TranslateService,
        @Optional() @Inject(NV_HIG_CONFIG) higConfig: NvHigConfig
    ) {
        this.logger = loggingService.getLogger('UserActivationGuard');
        this.defaultWarningMessage = higConfig?.userActivationGuardConfig?.buttonWarningMessage;

        if (higConfig?.userActivationGuardConfig) {
            this.logger.info(
                `UserActivationGuardConfig by HIG injection: ${JSON.stringify(higConfig.userActivationGuardConfig)}`
            );
        }

        if (this.isEnabled(higConfig?.userActivationGuardConfig?.link)) {
            this.guardedTypes.set(UserActivationGuardType.Link, higConfig.userActivationGuardConfig.link);
        }

        if (this.isEnabled(higConfig?.userActivationGuardConfig?.input)) {
            this.guardedTypes.set(UserActivationGuardType.Input, higConfig.userActivationGuardConfig.input);
        }

        if (this.isEnabled(higConfig?.userActivationGuardConfig?.playMedia)) {
            this.guardedTypes.set(UserActivationGuardType.PlayMedia, higConfig.userActivationGuardConfig.playMedia);
        }

        if (this.isEnabled(higConfig?.userActivationGuardConfig?.share)) {
            this.guardedTypes.set(UserActivationGuardType.Share, higConfig.userActivationGuardConfig.share);
        }
    }

    private isEnabled(activationType?: UserActivationType): boolean {
        return !!activationType && activationType !== 'none';
    }

    /**
     * Checks if the user has performed an active interaction with the page
     * (e.g., mouse click, key press) according to the User Activation API.
     * @description
     * This method verifies user activation status using the modern
     * `navigator.userActivation` interface when available.
     * @returns {boolean}
     * - `true` if user activation is currently active
     * - `false` if inactive or in unsupported browsers
     * @see [User Activation API - MDN](https://developer.mozilla.org/en-US/docs/Web/API/UserActivation)
     */
    isUserActivated(userActivationType: UserActivationType): boolean {
        switch (userActivationType) {
            case 'sticky':
                return !!navigator.userActivation?.hasBeenActive;
            case 'transient':
                return !!navigator.userActivation?.isActive;
            // just return true for none
            case 'none':
                return true;
            default:
                return false;
        }
    }

    /**
     * Applies monkey patches to browser APIs to enforce user activation checks.
     * This method iterates through the configured APIs (ApiPatchConfigs), saves their original implementations,
     * and replaces them with wrapped versions that first validate user activation status before proceeding.
     * @returns {void}
     * @remarks
     * - Modifies global browser APIs like window.open and navigator.share
     * - Automatically skips non-existent methods
     */
    guardSensitiveAPIs(): void {
        const self: UserActivationGuardService = this;
        if (!this.enabled) {
            return;
        }
        if (!navigator.userActivation) {
            this.logger.info('navigator.userActivation is not supported on this platform');
            return;
        }
        for (const config of ApiPatchConfigs) {
            const { object, methodName, type } = config;
            if (!this.guardedTypes.has(type)) {
                this.logger.info(`Skipping patching ${methodName}`);
                return;
            }
            if (!object || typeof object[methodName] !== 'function') {
                this.logger.error(`Failed patching ${methodName}: not found or not a function`);
                return;
            }

            const key = `${object.constructor.name}.${methodName}`;
            if (this.storedOriginalImplementation.has(key)) return;

            const descriptor = Object.getOwnPropertyDescriptor(object, methodName);

            if (!descriptor?.writable) {
                this.logger.error(`${methodName} is not a writable property for ${object}`);
                return;
            }

            // Store original implementation with methodName
            this.storedOriginalImplementation.set(key, {
                object,
                methodName, // Stored as string
                original: object[methodName],
                type
            });

            object[methodName] = function (...args: any[]) {
                const userActivationType = self.guardedTypes.get(type);
                if (!self.isUserActivated(userActivationType)) {
                    self.triggerWarningMsg();
                    // return a correct type of value based on the config so the consumer can handle it
                    return config.returnsPromise ? Promise.resolve(undefined) : undefined;
                }

                try {
                    const result = self.storedOriginalImplementation.get(key)?.original.apply(this, args);

                    // Check if the result is a Promise
                    if (result instanceof Promise) {
                        return result.catch(err => {
                            // Only catch NotAllowedError in the Promise rejection
                            if (err instanceof DOMException && err.name === 'NotAllowedError') {
                                // Handle the NotAllowedError as needed
                                self.logger.info('NotAllowedError caught: action is not user activated', err);
                                return Promise.resolve(undefined);
                            }
                            // Rethrow other Promise rejections
                            return Promise.reject(err);
                        });
                    }

                    // Return the original result for non-Promise returns
                    return result;
                } catch (err) {
                    self.catchNotAllowedError(err);
                }
            };
            this.patchedTypes.push(type);
        }
    }

    /**
     * Handles NotAllowedError exceptions thrown by browser APIs.
     * This method is called when a NotAllowedError occurs, indicating that the action
     * was not triggered by user activation.
     * @param err - The error object thrown by the API
     * @returns {void}
     */
    private catchNotAllowedError(err: any) {
        // Handle synchronous NotAllowedError
        if (err.name === 'NotAllowedError') {
            // Handle the synchronous NotAllowedError
            this.logger.info('NotAllowedError caught: action is not user activated', err);
            return undefined; // or any appropriate fallback
        }
        // Rethrow other synchronous errors
        throw err;
    }

    /**
     * Restores all patched browser APIs to their original implementations.
     * This method reverses the changes made by guardSensitiveAPIs(), ensuring no residual modifications
     * remain after service destruction.
     * @returns {void}
     * @remarks
     * - Should be called during service teardown or cleanup
     * - Maintains original method contexts and behaviors
     */
    restoreOriginalAPIs(): void {
        if (!this.enabled) {
            return;
        }
        if (!navigator.userActivation) {
            return;
        }
        this.storedOriginalImplementation.forEach(({ object, methodName, original }) => {
            try {
                object[methodName] = original;
            } catch (error) {
                this.logger.error(`Failed restoring ${methodName}:`, error);
            }
        });
        this.storedOriginalImplementation.clear();
        this.patchedTypes = [];
    }

    displayWarning(message: string, interpolateParams?: Object): MatSnackBarRef<TextOnlySnackBar> | null {
        const translatedMessage = this.translateService.instant(message, interpolateParams);

        if (this.warningHostElement) {
            if (!this.warningActive) {
                this.warningActive = true;
                this.showInlineWarning(translatedMessage);
            }
            return null;
        }

        return this.snackbarService.open(translatedMessage);
    }

    isGuarded(type: UserActivationGuardType) {
        return this.guardedTypes.has(type);
    }

    isPatched(type: UserActivationGuardType) {
        return this.patchedTypes.includes(type);
    }

    /**
     * Registers a host element for inline warning display. When set, warnings
     * render inside this element instead of using MatSnackBar. Automatically
     * cleans up when {@link destroy$} emits.
     *
     * @param hostElement - Container where the warning banner will be appended
     * @param destroy$ - Observable that signals cleanup (typically the component's destroy$)
     */
    setWarningHost(hostElement: HTMLElement, destroy$: Observable<void>): void {
        this.clearWarningHost();
        this.warningHostElement = hostElement;
        destroy$.pipe(take(1)).subscribe(() => this.clearWarningHost());
    }

    private clearWarningHost(): void {
        if (this.warningDismissTimer !== null) {
            clearTimeout(this.warningDismissTimer);
            this.warningDismissTimer = null;
        }
        if (this.warningEl && this.warningHostElement?.contains(this.warningEl)) {
            this.warningHostElement.removeChild(this.warningEl);
        }
        this.warningEl = null;
        this.labelEl = null;
        this.warningHostElement = null;
        this.warningActive = false;
    }

    private showInlineWarning(message: string): void {
        if (!this.warningHostElement) return;

        if (!this.warningEl) {
            this.warningEl = document.createElement('div');
            this.warningEl.classList.add(UserActivationGuardService.WARNING_CLASS);

            const container = document.createElement('div');
            container.classList.add('nv-hig', 'mat-mdc-snack-bar-container');

            const surface = document.createElement('div');
            surface.classList.add('mdc-snackbar__surface');

            this.labelEl = document.createElement('div');
            this.labelEl.classList.add('mdc-snackbar__label');
            this.labelEl.setAttribute('role', 'status');
            this.labelEl.setAttribute('aria-live', 'polite');

            surface.appendChild(this.labelEl);
            container.appendChild(surface);
            this.warningEl.appendChild(container);
            this.warningHostElement.appendChild(this.warningEl);
        }

        this.labelEl.textContent = message;
        this.warningEl.classList.add(UserActivationGuardService.VISIBLE_CLASS);

        if (this.warningDismissTimer !== null) {
            clearTimeout(this.warningDismissTimer);
        }
        this.warningDismissTimer = window.setTimeout(() => {
            this.warningDismissTimer = null;
            this.warningEl?.classList.remove(UserActivationGuardService.VISIBLE_CLASS);
            this.warningActive = false;
        }, UserActivationGuardService.AUTO_DISMISS_MS);
    }

    /**
     * prevent the original handler from being triggered and display an warning message if the event doesn't
     * have user activation.
     *
     * @example
     * const clickHandler = (e: MouseEvent) => { window.open('somewhere') } // open a new link upon called
     * const guardedClickHandler = UserActivationGuardService.guard(UserActivationType.link, clickhandler)
     *
     * @param type indicate the type of user activation api the handler is involved
     * @param handler an event handler calls APIs that need user activation
     * @param warningMessage the warning message being displayed in the snackbar
     * @param options.interpolationKey the key to be used for interpolation
     * @param options.text the text to be used for interpolation
     * @param options.useInnerText whether to use the inner text of the event target for interpolation
     * @returns an instrumented handler that takes over the control and checks if the event has an user activation before
     * the original handler.
     * if so, return control to the original handler, otherwise, display a snackbar to warning user and return.
     *
     */
    guard<T extends Event>(
        type: UserActivationGuardType,
        handler: (event: T, ...args: any[]) => any,
        warningMessage?: string,
        options?: UserActivationMessageOption
    ): (event: T, ...args: any[]) => any {
        if (!this.enabled || !this.isGuarded(type)) {
            return handler;
        }

        return (event: T, ...args: any[]): any => {
            if (!isEventUserActivated(event)) {
                this.triggerWarningMsg(event, warningMessage, options);
                const config = ApiPatchConfigs.find(c => c.type === type);
                return config && config.returnsPromise ? Promise.resolve(undefined) : undefined;
            }

            try {
                const result = handler(event, ...args);
                if (result && typeof result.then === 'function') {
                    return result.catch(err => {
                        if (err.name === 'NotAllowedError') {
                            this.logger.info('NotAllowedError caught in Promise: action is not user activated', err);
                            return Promise.resolve(undefined);
                        } else {
                            return Promise.reject(err);
                        }
                    });
                }
                // Return the original result for non-Promise returns
                return result;
            } catch (err) {
                this.catchNotAllowedError(err);
            }
        };
    }

    private triggerWarningMsg(event?: Event, warningMessage?: string, options?: UserActivationMessageOption) {
        const message = warningMessage ?? this.defaultWarningMessage;
        const interpolateParams = options?.interpolationKey
            ? {
                  [options.interpolationKey]: options?.useInnerText
                      ? (event?.target as HTMLElement)?.innerText
                      : options?.text
              }
            : undefined;

        // per ux requirements, the message shouldn't show up again if there is an existing one
        if (!this.snackBarOpened) {
            const snackbarRef = this.displayWarning(message, interpolateParams);

            if (snackbarRef) {
                merge(snackbarRef.afterOpened().pipe(mapTo(true)), snackbarRef.afterDismissed().pipe(mapTo(false)))
                    .pipe(take(2))
                    .subscribe(isOpened => {
                        this.snackBarOpened = isOpened;
                    });
            }
        }
    }

    guardObservable<T extends Event>(
        type: UserActivationGuardType,
        input: Observable<T>,
        warningMessage?: string,
        options?: UserActivationMessageOption
    ): Observable<T> {
        if (!this.enabled || !this.isGuarded(type)) {
            return input;
        }
        return input.pipe(
            tap(event => {
                const shouldBeGuarded = !isEventUserActivated(event);
                if (shouldBeGuarded) {
                    this.triggerWarningMsg(event, warningMessage, options);
                }
            }),
            catchError(err => {
                if (err.name === 'NotAllowedError') {
                    this.logger.info('NotAllowedError caught: action is not user activated', err);
                    return EMPTY;
                }
                // For all other errors, rethrow them to let them propagate
                return throwError(() => err);
            })
        );
    }
}
